import React, { useCallback, useEffect } from 'react';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@/components/Spacer';
import formatAmountWithCurrency from '@format/priceWithCurrency';
import { Currency } from '@/typings/api-models';
import { ApiDefinition } from '@documentsApi';
import { calculateMeansTest } from '@/tsClient/ai';
import { useToast } from '@/hooks';
import { ToastTypeVariants } from '@/context/ToastContext';
import AILabel from '@/components/AILabel';
import Modal from '@/components/Modal';
import { MeansTestAiResponse } from '@/tsClient/servers/noodleDocuments/document-api-models';
import s from './DocumentRequestUserDetail.module.scss';

type Props = {
    meansTestData: MeansTestAiResponse | null;
    tableData: ApiDefinition['/document-request-users/{documentRequestUserId}/consolidated-data']['GET']['response'] | null;
    selectedForMeanTest: string[];
    userWorkflowId: string | null;
    documentRequestUserId: string;
};

type RequestPayload = {
    "Amount per Pay Period": number | null;
    "Overtime per Pay Period": number | null;
    "Pay Date": string | null;
    "Pay Cadence": string | null;
    "Employer": string | null;
};

const MeansTestSection: React.FC<Props> = ({meansTestData, tableData, selectedForMeanTest, userWorkflowId, documentRequestUserId}) => {
  const [meansTestValue, setMeansTestValue] = React.useState<MeansTestAiResponse | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [isReasoningModalOpen, setIsReasoningModalOpen] = React.useState<boolean>(false);
  const toast = useToast();

  const ListItem = useCallback(({ label, value }) => (
    <div className={s.grossIncomeSection}>
      <span className='body-sm-bold'>{label}:</span>
      <span className='body-sm'>{value}</span>
    </div>
  ), []);

  const handleMeansTestCalculation = async (): Promise<void> => {
    if ( !tableData?.files?.length || !selectedForMeanTest?.length ) return;
    if (!userWorkflowId) {
      toast(ToastTypeVariants.ERROR, 'User workflow id is missing');
      return;
    }
    const payload: RequestPayload[] = [];
    tableData.files.forEach((file) => {
      if (file.documents?.length) {
        file.documents?.forEach((doc) => {
          if (selectedForMeanTest.includes(doc.id)) {
            payload.push({
              "Amount per Pay Period": (doc.data?.tableData as Record<string, number>).amountPerPayPeriod,
              "Employer": file.fileName,
              "Overtime per Pay Period": (doc.data?.tableData as Record<string, number>).overtimePerPayPeriod,
              "Pay Cadence": file.metadata?.payFrequency ?? 'monthly',
              "Pay Date": (doc.data?.tableData as Record<string, string>).payDate,
            });
          }
        });
      }
      else if( file.metadata && file.metadata.incomeSource && file.metadata.incomeSource !== 'Employment' && selectedForMeanTest.includes(file.id) ) {

        payload.push({
          "Amount per Pay Period": file.metadata.monthlyDollarValue,
          "Employer": file.fileName,
          "Overtime per Pay Period": 0,
          "Pay Cadence": file.metadata.payFrequency ?? 'monthly',
          "Pay Date": null,
        });

      }
    });

    try {
      setLoading(true);
      setMeansTestValue(null);
      const response = await calculateMeansTest({ data: payload, documentRequestUserId, userWorkflowId});
      setMeansTestValue(response);
    } catch (error) {
      toast(ToastTypeVariants.ERROR, 'Ai failed to calculate means test');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meansTestData) {
      setMeansTestValue(meansTestData);
    }
  },
  [meansTestData]);

  return (
    <div className={s.meansTestSectionContainer}>
      { isReasoningModalOpen
                && <Modal title={<span className={s['title--ai']}>Means Test Calculator AI Reasoning</span>} onClose={() => setIsReasoningModalOpen(false)}>
                  <div className={s.reasoningModalContent}>
                    { (loading && !meansTestValue)
                      ? <>
                        <Spacer />
                        <AILabel label="Glade ai is calculating..." />
                      </>
                      :<p>
                        {meansTestValue?.calculationExplanation}
                      </p>
                    }
                    <Spacer />
                    <Button
                      variant="ai"
                      size="sm"
                      fullWidth
                      disabled={loading}
                      onClick={handleMeansTestCalculation}>
                   Recalculate
                    </Button>
                  </div>
                </Modal>
      }
      <div className='caption-bold'>Means Test Calculator</div>
      {(loading && !meansTestValue)
        ? <>
          <Spacer />
          <AILabel label="Glade ai is calculating..." />
        </>
        :<>
          {meansTestValue?.grossMonthlyIncome && <>
            <Spacer />
            <ListItem label="2.Gross Monthly Income" value={formatAmountWithCurrency(meansTestValue.grossMonthlyIncome, Currency.Usd)} />
            <ListItem label="3.Alimony And Maintenance Payments" value={formatAmountWithCurrency(meansTestValue.alimonyAndMaintenancePayments, Currency.Usd)} />
            <ListItem label="7.Interest, Dividends And Royalties" value={formatAmountWithCurrency(meansTestValue.interestDividendsAndRoyalties, Currency.Usd)} />
            <ListItem label="8.Unemployment compensation" value={formatAmountWithCurrency(meansTestValue.unemploymentCompensation, Currency.Usd)} />
            <ListItem label="9.Pension or retirement income" value={formatAmountWithCurrency(meansTestValue.pensionOrRetirementIncome, Currency.Usd)} />
            <ListItem label="10.Income from all other sources not listed above" value={formatAmountWithCurrency(meansTestValue.otherHouseholdIncome, Currency.Usd)} />
            {/* <div className={s.grossIncomeSection}>
            <span className='body-sm-bold'>2.Gross Monthly Income:</span>
            <span className='body-sm'>{formatAmountWithCurrency(meansTestValue.grossMonthlyIncome, Currency.Usd)}</span>
            </div> */}
          </>}
          <Spacer />
          <div className={s.ctaSection}>
            {meansTestValue && <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsReasoningModalOpen(true)}>
                    View Reasoning
            </Button>}
            <Button
              variant="ai"
              size="sm"
              onClick={handleMeansTestCalculation}>
              {meansTestValue? 'Recalculate' : 'Calculate'}
            </Button>
          </div>
        </>
      }

    </div>
  );
};

export default MeansTestSection;